import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { useGetCustomerUserPhotoQuery } from '../../../_augmentt/services/customerGraphResources'
import { getInitialsFromName } from '../../util/table.util'
import styles from './UserAvatar.module.scss'

const VARIANT_CLASS = { primary: styles.primary, subtle: styles.subtle }
const SIZE_CLASS = { md: null, sm: styles.sm }

// Circular avatar for a Microsoft 365 directory user: shows the profile photo when available and
// falls back to the user's initials. The photo is fetched in the background and cached per customer
// and Graph object id, so the initials render immediately and the photo swaps in once it loads. On a
// missing or broken image the avatar shows initials. Decorative: callers render the name beside it.
function UserAvatar({ customerId, userId, name = '', enabled = true, size = 'md', variant = 'primary', className }) {
  const shouldFetchPhoto = enabled && !!customerId && !!userId
  // currentData is cleared while skipped or loading new args, so another user's photo never shows.
  const { currentData: photo } = useGetCustomerUserPhotoQuery({ customerId, userId }, { skip: !shouldFetchPhoto })
  const [brokenPhoto, setBrokenPhoto] = useState(null)

  const handlePhotoError = useCallback(() => setBrokenPhoto(photo), [photo])

  const sizeClass = SIZE_CLASS[size]

  if (photo && photo !== brokenPhoto) {
    return <img className={clsx(styles.avatar, sizeClass, className)} src={photo} alt="" onError={handlePhotoError} />
  }

  const initials = getInitialsFromName(name)
  return (
    <span
      className={clsx(
        'd-inline-flex align-items-center justify-content-center',
        styles.avatar,
        sizeClass,
        VARIANT_CLASS[variant],
        className,
      )}
      aria-hidden="true"
    >
      {initials ?? <i className="far fa-user" />}
    </span>
  )
}

UserAvatar.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  userId: PropTypes.string,
  name: PropTypes.string,
  enabled: PropTypes.bool,
  size: PropTypes.oneOf(['md', 'sm']),
  variant: PropTypes.oneOf(['primary', 'subtle']),
  className: PropTypes.string,
}

export default React.memo(UserAvatar)
